import { useState, useEffect } from 'react';
import { getProducts, getMakes } from '../../api';
import { Card, CardHeader, CardContent, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Package, CheckCircle, XCircle, Factory, TrendingUp, ShoppingCart } from 'lucide-react';

export default function Dashboard() {
  const [stats, setStats] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const [productsData, makesData] = await Promise.all([
          getProducts({ page: 1, limit: 1000 }),
          getMakes(),
        ]);

        const prods = productsData.products;
        setProducts(prods);
        setStats([
          {
            label: 'Total Products',
            value: prods.length,
            icon: Package,
            color: 'text-indigo-600',
            bg: 'bg-indigo-50',
          },
          {
            label: 'Available',
            value: prods.filter(p => p.isAvailable).length,
            icon: CheckCircle,
            color: 'text-green-600',
            bg: 'bg-green-50',
          },
          {
            label: 'Unavailable',
            value: prods.filter(p => !p.isAvailable).length,
            icon: XCircle,
            color: 'text-red-600',
            bg: 'bg-red-50',
          },
          {
            label: 'Total Makes',
            value: makesData.length,
            icon: Factory,
            color: 'text-amber-600',
            bg: 'bg-amber-50',
          },
        ]);
      } catch (err) {
        console.error('Failed to load dashboard stats:', err);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  // Count products by part type
  const typeCounts = {};
  products.forEach(p => {
    typeCounts[p.partType] = (typeCounts[p.partType] || 0) + 1;
  });

  const typeEntries = Object.entries(typeCounts).sort((a, b) => b[1] - a[1]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <p className="text-sm text-slate-400">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">Dashboard</h1>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <Card key={i} className="border bg-white shadow-sm">
              <CardContent className="p-5">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${stat.bg}`}>
                    <Icon className={`w-5 h-5 ${stat.color}`} />
                  </div>
                  <div>
                    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{stat.label}</p>
                    <p className={`text-2xl font-bold ${stat.color}`}>{stat.value}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Products by Type */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              Products by Type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {typeEntries.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-5">No data yet</p>
            ) : (
              <div className="space-y-3">
                {typeEntries.map(([type, count]) => (
                  <div key={type} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <span className="text-sm text-slate-600">{type}</span>
                    <Badge variant="default">{count}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Products */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ShoppingCart className="w-4 h-4" />
              Recent Products
            </CardTitle>
          </CardHeader>
          <CardContent>
            {products.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-5">
                No products yet. Seed the database to get started.
              </p>
            ) : (
              <div className="space-y-3">
                {products.slice(-5).reverse().map(p => (
                  <div key={p._id} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">
                        {p.title.length > 30 ? p.title.substring(0, 30) + '...' : p.title}
                      </p>
                      <p className="text-xs text-slate-400">{p.partCategory} · {p.condition}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-4">
                      <Badge variant={p.isAvailable ? 'default' : 'destructive'}>
                        {p.isAvailable ? 'Available' : 'Unavailable'}
                      </Badge>
                      <span className="text-sm font-semibold text-slate-900">
                        ${p.price.toLocaleString('en-CA')}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}